/* eslint-disable formatjs/no-literal-string-in-jsx */
import React, { FunctionComponent } from 'react'
import { GestureResponderEvent, Platform, ScrollView, View } from 'react-native'

import { capitalize, noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { secureMicrositesMetadataList } from '@lyrahealth-inc/shared-app-logic/src/features/CMSMicrosite/constants'
import { CMSMicrositeMetadata } from '@lyrahealth-inc/shared-app-logic/src/features/CMSMicrosite/types'
import { ProgramCustomerPropertyId } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customerProgram'
import { Theme } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/theme'
import { DEV_TOOLS_SECTION_ID } from '@lyrahealth-inc/shared-app-logic/src/features/devTools/types'
import { LDFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { getAge } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import {
  DEFAULT_SMART_SEARCH_CONFIG,
  SmartSearchConfig,
} from '@lyrahealth-inc/shared-app-logic/src/features/smartSearch/constants'
import { WELLNESS_TOOLKIT_PROMOTION } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessToolkit/types'
import { HealthPlansList, User, YearProgramCoverage } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import { AuthForm } from './auth/AuthForm'
import { PersistStoreForm } from './persistStore/PersistStoreForm'
import { SearchConfigForm } from './smartSearch/SearchConfigForm'
import { TranslationsForm } from './translations/TranslationsForm'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { CopyToClipboard } from '../../atoms/copyToClipboard/CopyToClipboard'
import { CloseIcon } from '../../atoms/icons/CloseIcon'
import { DownloadFileIcon } from '../../atoms/icons/DownloadFileIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { Title } from '../../atoms/title/Title'
import { IS_CYPRESS_MOBILE, IS_WEB } from '../../constants'
import { Accordion } from '../../molecules/accordion/Accordion'
import { ToggleSwitch } from '../../molecules/toggleSwitch/ToggleSwitch'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { Flex1SafeAreaView, Flex1View } from '../../templates/content/CommonViews'
import { ContentAreaView } from '../../templates/content/ContentArea'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface DevToolsProps {
  persistItems?: Dict
  persistStoreKeys?: Record<string, string>
  isLoggedIn: boolean
  idTokenExpiration: number
  inactivityExpiration: number
  debugLang: string
  enableMixpanelDebug: boolean
  releaseUuid?: string | null
  onClose: () => void
  onNavigateToSearchForCare: () => void
  onNavigateToChildSearchForCare: () => void
  onNavigateToForkedTriage: () => void
  onNavigateToCourseCompletionCertScreen: () => void
  onNavigateToElectionMicrosite: () => void
  onNavigateToCMSMicrosite: (metadata: CMSMicrositeMetadata) => void
  onNavigateToToolkit: (promotion: WELLNESS_TOOLKIT_PROMOTION) => void
  onNavigateToNeurodiversityToolkit: () => void
  onNavigateToWomensHealthToolkit: () => void
  onNavigateToHealthChallengesToolkit: () => void
  onNavigateToSetUpAccount: () => void
  onNavigateToSmartSearch?: () => void
  onNavigateToIntlDebugScreen?: () => void
  onClearLanguageCache?: () => void
  onRefreshTranslations?: () => void
  onToggleEnableMixpanelDebug: () => void
  smartSearchShowDebug?: boolean
  onToggleSmartSearchShowDebug?: () => void
  smartSearchConfig?: SmartSearchConfig
  /** Base config from LaunchDarkly (used for comparison indicator and reset) */
  smartSearchBaseConfig?: SmartSearchConfig
  onSmartSearchConfigChange?: (config: SmartSearchConfig) => void
  onResetSmartSearchConfig?: () => void
  onPersistStoreClearKeys?: (values: Dict) => void
  onPersistStoreClearAll?: (event: GestureResponderEvent) => void
  setDebugLang?: (debugLang: string) => void
  onAuthSubmit?: (values: { idTokenExpiration: string; inactivityExpiration: string }) => void
  onPressDeleteAllSearches: () => void
  accountDetails: {
    userId: string
    email: string
    customerName: string
    country: string
    language: string
    dependents: string[] | User[]
  }
  featureFlags: LDFlags | Dict
  healthPlanAndEligibilityData: {
    programCoverageBreakdown?: YearProgramCoverage
    programDetails: { [key in ProgramCustomerPropertyId]?: { isEnabled: boolean } }
    healthPlanInformation: {
      healthPlanData?: Dict
      healthPlanConfig?: HealthPlansList
      healthPlanExclude?: string[]
      healthPlanSections?: { title: string; properties: string[] }[]
      healthPlanPriorityOrder?: string[]
    }
  }
  mixpanelActivity?: any[]
  onDownloadDevToolsData: () => void
  searchForCareData: {
    onboardData: Dict
    triageData: Dict
    onboardExclude: string[]
    triageDataExclude: string[]
    triagePriorityOrder: string[]
    onboardPriorityOrder: string[]
  }
  activeMicrositesMetadataList: CMSMicrositeMetadata[]
}

export const devToolsSidePanelStyle = {
  overflow: 'auto',
  maxHeight: '100vh',
  alignItems: 'unset',
  maxWidth: '600px',
  width: '100%',
}

const MainHeader = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingHorizontal: theme.spacing['20px'],
  paddingTop: theme.breakpoints.isMobileSized ? theme.spacing['24px'] : 0,
}))

const IconContainer = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  paddingBottom: theme.spacing['20px'],
}))

const SectionContainer = styled.View({
  padding: '15px 0px',
})

const SectionContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['16px'],
  backgroundColor: theme.colors.backgroundSecondary,
  borderRadius: '20px',
}))

const DevTitleContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  borderRadius: '20px',
  marginBottom: theme.spacing['20px'],
  padding: theme.spacing['16px'],
  backgroundColor: theme.colors.backgroundHighlightSecondary,
}))

const SectionTitle = styled(Title)({
  marginBottom: '16px',
})

const ButtonsContainer = styled(Flex1View)<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing['8px'],
}))

const FeatureFlagsContainer = styled(ScrollView)<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['16px'],
  maxHeight: '300px',
  backgroundColor: theme.colors.backgroundHighlightTertiary,
  borderRadius: '20px',
}))

const ProgramBreakdownItem = styled.View({
  marginBottom: '16px',
})

const JsonDisplayItem = styled.View({
  marginBottom: '16px',
})

const SpaceBetweenContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const MixpanelActivityContainer = styled(ScrollView)<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['16px'],
  maxHeight: '300px',
  backgroundColor: theme.colors.backgroundHighlightTertiary,
  borderRadius: '20px',
}))

const JsonContainer = styled(ScrollView)<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundHighlightTertiary,
  borderRadius: '20px',
  padding: theme.spacing['16px'],
  maxHeight: '200px',
  marginTop: theme.spacing['4px'],
}))

export const DevTools: FunctionComponent<DevToolsProps> = ({
  accountDetails,
  featureFlags,
  persistItems,
  persistStoreKeys,
  isLoggedIn,
  idTokenExpiration,
  inactivityExpiration,
  debugLang,
  enableMixpanelDebug,
  releaseUuid,
  onClose,
  onNavigateToSearchForCare,
  onNavigateToChildSearchForCare,
  onNavigateToForkedTriage,
  onNavigateToCourseCompletionCertScreen,
  onNavigateToElectionMicrosite,
  onNavigateToCMSMicrosite,
  onNavigateToSetUpAccount,
  onNavigateToSmartSearch = noop,
  onNavigateToToolkit,
  onNavigateToNeurodiversityToolkit,
  onNavigateToWomensHealthToolkit,
  onNavigateToHealthChallengesToolkit,
  onNavigateToIntlDebugScreen = noop,
  onClearLanguageCache = noop,
  onRefreshTranslations = noop,
  onToggleEnableMixpanelDebug,
  smartSearchShowDebug = true,
  onToggleSmartSearchShowDebug = noop,
  smartSearchConfig = DEFAULT_SMART_SEARCH_CONFIG,
  smartSearchBaseConfig = DEFAULT_SMART_SEARCH_CONFIG,
  onSmartSearchConfigChange = noop,
  onResetSmartSearchConfig = noop,
  onPersistStoreClearKeys = noop,
  onPersistStoreClearAll = noop,
  setDebugLang = noop,
  healthPlanAndEligibilityData,
  mixpanelActivity,
  onDownloadDevToolsData,
  searchForCareData,
  onAuthSubmit = noop,
  activeMicrositesMetadataList,
  onPressDeleteAllSearches,
}) => {
  const { colors, themeType, breakpoints, setThemeType, isAuroraTheme, toggleIsAuroraTheme, isLyraSelect } = useTheme()
  const getIsEnabledIcon = (isEnabled: boolean) => (isEnabled ? '✅' : '❌')
  const programDescriptions: { [key in ProgramCustomerPropertyId]?: string } = {
    alcoholUseDisorderTherapy: 'Alcohol Use Disorder Therapy',
    blendedCareMeds: 'LC Meds',
    considerLyraTherapy: 'LC Therapy',
    stressManagement: 'LC Coaching',
    coachingForParents: 'Coaching for Parents',
    considerMeds: 'DA Meds',
    directAccessTherapy: 'DA Therapy',
    guidedSelfCareEnabled: 'Guided Self Care',
    clinicalLeaveEvaluation: 'Clinical Leave Evaluation',
  }
  const { userId, email, customerName, country, language, dependents } = accountDetails

  const getJsonDisplayItem = (key: string, value: any) => {
    const displayValue = JSON.stringify(value, null, 2)
    return (
      <JsonDisplayItem key={key}>
        <SpaceBetweenContainer>
          <BodyText text={`${key}`} />
          <View>
            <CopyToClipboard copyText={displayValue} />
          </View>
        </SpaceBetweenContainer>
        <JsonContainer>
          <BodyText
            text={`${displayValue}`}
            color={colors.textSecondary}
            style={{
              fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
            }}
          />
        </JsonContainer>
      </JsonDisplayItem>
    )
  }

  const sortData = (data: Dict, priorityOrder: string[] = []) => {
    return Object.entries(data).sort(([keyA], [keyB]) => {
      const priorityA = priorityOrder.indexOf(keyA)
      const priorityB = priorityOrder.indexOf(keyB)

      if (priorityA !== -1 && priorityB !== -1) {
        return priorityA - priorityB
      }

      if (priorityA !== -1) return -1

      if (priorityB !== -1) return 1

      return keyA.localeCompare(keyB)
    })
  }

  const getSearchForCareSection = (data: Dict, toExclude: string[] = [], priorityOrder: string[] = []) => {
    const sortedEntries = sortData(data, priorityOrder)

    return Object.keys(data).length ? (
      <SectionContainer>
        {sortedEntries.map(([key, value]) => {
          const isEmpty = value && typeof value === 'object' && Object.keys(value).length === 0
          return value && !isEmpty && !toExclude.includes(key) ? getJsonDisplayItem(key, value) : null
        })}
      </SectionContainer>
    ) : (
      <></>
    )
  }

  const getHealthPlanSections = () => {
    const originalData = healthPlanAndEligibilityData.healthPlanInformation.healthPlanData
    const priorityOrder = healthPlanAndEligibilityData.healthPlanInformation.healthPlanPriorityOrder || []
    const sections = healthPlanAndEligibilityData.healthPlanInformation.healthPlanSections || []
    const healthPlanData = { ...originalData }
    const toExclude = healthPlanAndEligibilityData.healthPlanInformation.healthPlanExclude || []

    toExclude.forEach((key) => {
      delete healthPlanData[key]
    })
    const sectionData: { title: string; data: Dict | HealthPlansList }[] = []
    if (healthPlanAndEligibilityData.healthPlanInformation.healthPlanConfig) {
      sectionData.push({
        title: 'Current Health Plan',
        data: healthPlanAndEligibilityData.healthPlanInformation.healthPlanConfig,
      })
    }
    sections.forEach((section) => {
      sectionData.push({
        title: section.title,
        data: section.properties.reduce((acc, key) => {
          if (!healthPlanData[key]) return acc
          acc[key] = healthPlanData[key]
          delete healthPlanData[key]
          return acc
        }, {}),
      })
    })

    const sortedHealthPlanData = sortData(healthPlanData, priorityOrder)

    const getId = (title: string) => {
      const id = title
        .split(' ')
        .map((word, index) => (index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1)))
        .join('')
      return tID(`DevTools-subsection-${id}`)
    }

    return [
      {
        title: 'Health Plan',
        component: (
          <SectionContainer testID={getId('Health Plan')}>
            {sortedHealthPlanData.map(([key, value]) => {
              return getJsonDisplayItem(key, value)
            })}
          </SectionContainer>
        ),
      },
      ...sectionData
        .filter((section) => Object.keys(section.data).length > 0)
        .map((section) => ({
          title: section.title,
          component: (
            <SectionContainer testID={getId(section.title)}>
              {Object.entries(section.data).map(([key, value]) => {
                return getJsonDisplayItem(key, value)
              })}
            </SectionContainer>
          ),
        })),
    ]
  }

  const isMobile = !IS_WEB || IS_CYPRESS_MOBILE

  const handleNavigateAndClose = (navigationFunction: () => void) => {
    navigationFunction()
    onClose()
  }

  const sections: {
    id: DEV_TOOLS_SECTION_ID | string
    title: string
    component?: React.JSX.Element
    shouldShow: boolean
    subsections?: { id: DEV_TOOLS_SECTION_ID | string; title: string; component: React.JSX.Element }[]
  }[] = [
    {
      id: DEV_TOOLS_SECTION_ID.ACCOUNT_DETAILS,
      title: 'Account Details 📝',
      subsections: [
        {
          id: DEV_TOOLS_SECTION_ID.ACCOUNT_DETAILS_LYRA_ID,
          title: 'Lyra ID 🪪',
          component: (
            <SpaceBetweenContainer>
              <BodyText size={BodyTextSize.DEFAULT} text={userId} />
              <View>
                <CopyToClipboard copyText={userId} />
              </View>
            </SpaceBetweenContainer>
          ),
        },
        {
          id: DEV_TOOLS_SECTION_ID.ACCOUNT_DETAILS_BASIC_INFO,
          title: 'Basic Info 🏷️',
          component: (
            <>
              <BodyText size={BodyTextSize.DEFAULT} text={`Email: ${email}`} />
              <BodyText size={BodyTextSize.DEFAULT} text={`Customer: ${customerName}`} />
              <BodyText size={BodyTextSize.DEFAULT} text={`Country: ${country}`} />
              <BodyText size={BodyTextSize.DEFAULT} text={`Language: ${language}`} />
            </>
          ),
        },
        {
          id: DEV_TOOLS_SECTION_ID.ACCOUNT_DETAILS_DEPENDENTS,
          title: 'Dependents 🧑‍🧑‍🧒‍🧒',
          component: (
            <>
              {dependents && dependents.length > 0 ? (
                dependents.map((dependent: string | User) =>
                  typeof dependent === 'string' ? (
                    <BodyText key={dependent} size={BodyTextSize.DEFAULT} text={`• ${dependent}`} />
                  ) : (
                    <BodyText
                      key={dependent.id}
                      size={BodyTextSize.DEFAULT}
                      text={`• ${dependent.firstname} ${dependent.lastname} - ${
                        dependent.dob
                          ? `Age ${getAge({
                              date: dependent.dob,
                              dateFormat: 'MM/DD/YYYY',
                            })}`
                          : ''
                      }`}
                    />
                  ),
                )
              ) : (
                <BodyText size={BodyTextSize.DEFAULT} text={'No dependents'} />
              )}
            </>
          ),
        },
      ],
      shouldShow: isLoggedIn,
    },
    {
      id: DEV_TOOLS_SECTION_ID.SEARCH_FOR_CARE,
      title: 'Search for Care 🫶',
      subsections: [
        {
          id: DEV_TOOLS_SECTION_ID.SEARCH_FOR_CARE_TRIAGE,
          title: 'Triage',
          component: getSearchForCareSection(
            searchForCareData.triageData,
            searchForCareData.triageDataExclude,
            searchForCareData.triagePriorityOrder,
          ),
        },
        ...((Object.keys(searchForCareData.onboardData).length && [
          {
            id: DEV_TOOLS_SECTION_ID.SEARCH_FOR_CARE_OTHER,
            title: 'Other',
            component: getSearchForCareSection(
              searchForCareData.onboardData,
              searchForCareData.onboardExclude,
              searchForCareData.onboardPriorityOrder,
            ),
          },
        ]) ||
          []),
      ],
      shouldShow: Boolean(searchForCareData.triageData.selectedSearchId),
    },
    {
      id: DEV_TOOLS_SECTION_ID.SEARCH_FOR_CARE_USER,
      title: 'Search for Care User Actions ❤️‍🩹',
      subsections: [
        {
          id: DEV_TOOLS_SECTION_ID.SEARCH_FOR_CARE_OTHER,
          title: '⚠️ DELETE ALL SEARCHES FROM TRS (THIS ACTION CANNOT BE UNDONE)',
          component: (
            <SecondaryButton text='Delete all triage searches for this user' onPress={onPressDeleteAllSearches} />
          ),
        },
      ],
      shouldShow: isLoggedIn,
    },
    {
      id: DEV_TOOLS_SECTION_ID.THEMING,
      title: 'Theming 🌈',
      subsections: [
        {
          id: DEV_TOOLS_SECTION_ID.THEMING_THEME_TOGGLE,
          title: 'Theme Toggle',
          component: (
            <SectionContainer>
              <SpaceBetweenContainer>
                <BodyText text='Enable Dark Mode' />
                <ToggleSwitch
                  isOn={themeType === Theme.DARK}
                  onPress={() => setThemeType(themeType === Theme.DARK ? Theme.LIGHT : Theme.DARK)}
                />
              </SpaceBetweenContainer>
              <SpaceBetweenContainer style={{ marginTop: 8 }}>
                <BodyText text='Use System Theme' />
                <ToggleSwitch
                  isOn={themeType === Theme.SYSTEM}
                  onPress={() => setThemeType(themeType === Theme.SYSTEM ? Theme.LIGHT : Theme.SYSTEM)}
                />
              </SpaceBetweenContainer>
              {!isLyraSelect && (
                <SpaceBetweenContainer style={{ marginTop: 8 }}>
                  <BodyText text='Enable Aurora Theme' />
                  <ToggleSwitch isOn={isAuroraTheme} onPress={toggleIsAuroraTheme} />
                </SpaceBetweenContainer>
              )}
            </SectionContainer>
          ),
        },
      ],
      shouldShow: true,
    },
    {
      id: DEV_TOOLS_SECTION_ID.FEATURE_FLAGS,
      title: 'Feature Flags 🚩',
      subsections: [
        {
          id: DEV_TOOLS_SECTION_ID.FEATURE_FLAGS_ENABLED,
          title: 'Enabled Flags',
          component: (
            <FeatureFlagsContainer>
              {Object.entries(featureFlags)
                .filter(([, flagValue]) => flagValue === true)
                .map(([flagName]) => (
                  <BodyText
                    key={flagName}
                    size={BodyTextSize.DEFAULT}
                    text={`✅ ${flagName}`}
                    color={colors.textSuccess}
                  />
                ))}
            </FeatureFlagsContainer>
          ),
        },
        {
          id: DEV_TOOLS_SECTION_ID.FEATURE_FLAGS_DISABLED,
          title: 'Disabled Flags',
          component: (
            <FeatureFlagsContainer>
              {Object.entries(featureFlags)
                .filter(([, flagValue]) => flagValue === false)
                .map(([flagName]) => (
                  <BodyText
                    key={flagName}
                    size={BodyTextSize.DEFAULT}
                    text={`❌ ${flagName}`}
                    color={colors.textInactive}
                  />
                ))}
            </FeatureFlagsContainer>
          ),
        },
        {
          id: DEV_TOOLS_SECTION_ID.FEATURE_FLAGS_COMPLEX,
          title: 'Complex Flags',
          component: (
            <FeatureFlagsContainer>
              {Object.entries(featureFlags)
                .filter(([, flagValue]) => typeof flagValue !== 'boolean')
                .map(([flagName, flagValue]) => (
                  <BodyText
                    key={flagName}
                    size={BodyTextSize.DEFAULT}
                    text={`🔧 ${flagName}: ${
                      typeof flagValue === 'object' ? JSON.stringify(flagValue) : String(flagValue)
                    }`}
                  />
                ))}
            </FeatureFlagsContainer>
          ),
        },
      ],
      shouldShow: true,
    },
    {
      id: DEV_TOOLS_SECTION_ID.QUICK_NAVIGATION,
      title: 'Quick Navigation 📍',
      subsections: [
        {
          id: DEV_TOOLS_SECTION_ID.QUICK_NAVIGATION_SEARCH_FOR_CARE,
          title: 'Search for Care',
          component: (
            <ButtonsContainer>
              <SecondaryButton
                isSmall
                text='Search for Care'
                onPress={() => handleNavigateAndClose(onNavigateToSearchForCare)}
                testID={tID('DevTools-quickNavigation-searchForCareButton')}
              />
              <SecondaryButton
                isSmall
                text='Child Search for Care'
                onPress={() => handleNavigateAndClose(onNavigateToChildSearchForCare)}
                testID={tID('DevTools-quickNavigation-searchForCareChildButton')}
              />
            </ButtonsContainer>
          ),
        },
        {
          id: DEV_TOOLS_SECTION_ID.QUICK_NAVIGATION_MICROSITES,
          title: 'Microsites',
          component: (
            <ButtonsContainer>
              <SecondaryButton
                isSmall
                text='Election Microsite'
                onPress={() => handleNavigateAndClose(onNavigateToElectionMicrosite)}
              />
              {[...activeMicrositesMetadataList, ...secureMicrositesMetadataList].map((metadata) => {
                return (
                  <SecondaryButton
                    isSmall
                    text={`${metadata.micrositeId} (CMS)`}
                    key={metadata.micrositeId}
                    onPress={() => handleNavigateAndClose(() => onNavigateToCMSMicrosite(metadata))}
                    testID={tID(`DevTools-quickNavigation-${metadata.micrositeId}Button`)}
                  />
                )
              })}
            </ButtonsContainer>
          ),
        },
        {
          id: DEV_TOOLS_SECTION_ID.QUICK_NAVIGATION_WELLNESS_TOOLKITS,
          title: 'Wellness Toolkits',
          component: (
            <ButtonsContainer>
              {Object.values(WELLNESS_TOOLKIT_PROMOTION).map((key) => {
                return (
                  <SecondaryButton
                    isSmall
                    text={
                      key === WELLNESS_TOOLKIT_PROMOTION.FAMILY
                        ? 'Family Hub'
                        : `${capitalize(key).replaceAll('-', ' ')}`
                    }
                    key={key}
                    onPress={() => handleNavigateAndClose(() => onNavigateToToolkit(key as WELLNESS_TOOLKIT_PROMOTION))}
                    testID={tID(`DevTools-quickNavigation-${key}Button`)}
                  />
                )
              })}
            </ButtonsContainer>
          ),
        },
        {
          id: DEV_TOOLS_SECTION_ID.QUICK_NAVIGATION_CENTER_FOR_EXCELLENCE_TOOLKITS,
          title: 'Center for Excellence Toolkits',
          component: (
            <ButtonsContainer>
              <SecondaryButton
                isSmall
                text={'Neurodiversity Toolkit'}
                onPress={() => handleNavigateAndClose(() => onNavigateToNeurodiversityToolkit())}
                testID={tID(`DevTools-quickNavigation-neuroDiversityToolkitButton`)}
              />
              <SecondaryButton
                isSmall
                text={'Womens Health Toolkit'}
                onPress={() => handleNavigateAndClose(() => onNavigateToWomensHealthToolkit())}
                testID={tID(`DevTools-quickNavigation-womensHealthToolkitButton`)}
              />
              <SecondaryButton
                isSmall
                text={'Health Challenges Toolkit'}
                onPress={() => handleNavigateAndClose(() => onNavigateToHealthChallengesToolkit())}
                testID={tID(`DevTools-quickNavigation-healthChallengesToolkitButton`)}
              />
            </ButtonsContainer>
          ),
        },
        {
          id: DEV_TOOLS_SECTION_ID.QUICK_NAVIGATION_OTHER,
          title: '',
          component: (
            <ButtonsContainer>
              <SecondaryButton
                isSmall
                text='Forked Triage'
                onPress={() => handleNavigateAndClose(onNavigateToForkedTriage)}
                testID={tID(`DevTools-quickNavigation-forkedTriageButton`)}
              />
              <SecondaryButton
                isSmall
                text='Course Completion Certificate'
                onPress={() => handleNavigateAndClose(onNavigateToCourseCompletionCertScreen)}
                testID={tID(`DevTools-quickNavigation-courseCompletionCertButton`)}
              />
              {!isLoggedIn && (
                <SecondaryButton
                  isSmall
                  text='Set Up Account'
                  onPress={() => handleNavigateAndClose(onNavigateToSetUpAccount)}
                  testID={tID(`DevTools-quickNavigation-setUpAccountButton`)}
                />
              )}
              <SecondaryButton
                isSmall
                text='Smart Search (V0)'
                onPress={() => handleNavigateAndClose(onNavigateToSmartSearch)}
                testID={tID(`DevTools-quickNavigation-smartSearchButton`)}
              />
            </ButtonsContainer>
          ),
        },
      ],
      shouldShow: true,
    },
    ...getHealthPlanSections().map((section) => ({
      id: `${DEV_TOOLS_SECTION_ID.HPI_ELIGIBILITY}-${section.title}`,
      title: `HPI / Eligibility - ${section.title} 🌱`,
      component: section.component,
      shouldShow: isMobile,
    })),
    {
      id: DEV_TOOLS_SECTION_ID.HPI_ELIGIBILITY,
      title: 'HPI / Eligibility 🌱',
      component: (() => {
        if (isMobile) {
          return undefined
        }
        const sections = getHealthPlanSections()
        return (
          <>
            {sections.map((section) => {
              return (
                <Accordion
                  key={section.title}
                  title={section.title}
                  content={section.component}
                  initiallyOpen={false}
                  backgroundColor={colors.backgroundSecondary}
                  showGradient={false}
                />
              )
            })}
          </>
        )
      })(),
      subsections: [
        {
          id: DEV_TOOLS_SECTION_ID.HPI_ELIGIBILITY_PROGRAM_COVERAGE,
          title: 'Program Coverage',
          component: (
            <SectionContainer>
              {healthPlanAndEligibilityData.programCoverageBreakdown &&
                Object.entries(healthPlanAndEligibilityData.programCoverageBreakdown).map(
                  ([program, programSupport]) => {
                    const programName = program as ProgramCustomerPropertyId
                    const programDetails = healthPlanAndEligibilityData.programDetails[programName]
                    return (
                      <ProgramBreakdownItem key={program}>
                        <Title
                          text={`${programDescriptions[programName]}`}
                          size={TitleSize.SMALL}
                          color={colors.textActive}
                        />
                        <Title
                          text={`${program}${!programDetails?.isEnabled ? ' (Disabled)' : ''}`}
                          size={TitleSize.XSMALL}
                          color={colors.textInactive}
                        />

                        {programDetails?.isEnabled && (
                          <BodyText
                            text={`EAP: ${getIsEnabledIcon(programSupport.eapSupported)} / HPI: ${getIsEnabledIcon(
                              programSupport.bhbSupported || programSupport.specialtyBhbSupported,
                            )}`}
                            color={colors.textPrimary}
                          />
                        )}
                      </ProgramBreakdownItem>
                    )
                  },
                )}
            </SectionContainer>
          ),
        },
      ],
      shouldShow: true,
    },
    {
      id: DEV_TOOLS_SECTION_ID.MIXPANEL,
      title: 'Mixpanel 📈',
      subsections: [
        {
          id: DEV_TOOLS_SECTION_ID.MIXPANEL_DEBUG,
          title: `Debug Console Logs - Currently ${enableMixpanelDebug ? `enabled` : 'disabled'} ${getIsEnabledIcon(
            enableMixpanelDebug,
          )}`,
          component: (
            <SectionContainer>
              <SpaceBetweenContainer>
                {enableMixpanelDebug ? (
                  <SecondaryButton
                    text='Disable'
                    onPress={onToggleEnableMixpanelDebug}
                    testID={tID('DevTools-mixpanelDebugDisableButton')}
                  />
                ) : (
                  <PrimaryButton
                    text='Enable'
                    onPress={onToggleEnableMixpanelDebug}
                    testID={tID('DevTools-mixpanelDebugEnableButton')}
                  />
                )}
              </SpaceBetweenContainer>
            </SectionContainer>
          ),
        },
        {
          id: DEV_TOOLS_SECTION_ID.MIXPANEL_EVENT_HISTORY,
          title: 'Event history',
          component: (
            <MixpanelActivityContainer>
              {mixpanelActivity?.map((activity, index) => (
                <BodyText key={index} text={`• ${activity.event}`} />
              ))}
            </MixpanelActivityContainer>
          ),
        },
      ],
      shouldShow: true,
    },
    {
      id: DEV_TOOLS_SECTION_ID.DEBUG_TOOLS,
      title: 'Debug Tools 🛠️',
      subsections: [
        {
          id: DEV_TOOLS_SECTION_ID.DEBUG_TOOLS_SMART_SEARCH,
          title: 'Smart Search 🔍',
          component: (
            <View>
              <SectionContainer
                testID={tID(`DevTools-subsection-${DEV_TOOLS_SECTION_ID.DEBUG_TOOLS_SMART_SEARCH_SHOW_DEBUG}`)}
              >
                <SpaceBetweenContainer>
                  <BodyText text={`Show Debug ${getIsEnabledIcon(smartSearchShowDebug)}`} size={BodyTextSize.DEFAULT} />
                  <ToggleSwitch
                    isOn={smartSearchShowDebug}
                    onPress={() => onToggleSmartSearchShowDebug()}
                    testID={'DevTools-smartSearchShowDebugToggle'}
                  />
                </SpaceBetweenContainer>
              </SectionContainer>
              <SectionContainer testID={tID('DevTools-subsection-smartSearchConfig')}>
                <SearchConfigForm
                  config={smartSearchConfig}
                  baseConfig={smartSearchBaseConfig}
                  onConfigChange={onSmartSearchConfigChange}
                  onReset={onResetSmartSearchConfig}
                />
              </SectionContainer>
            </View>
          ),
        },
      ],
      shouldShow: true,
    },
    {
      id: DEV_TOOLS_SECTION_ID.RELEASE_UUID,
      title: 'Release UUID 🚢',
      component: <BodyText text={releaseUuid ?? 'No update UUID found'} size={BodyTextSize.DEFAULT} />,
      shouldShow: Boolean(releaseUuid),
    },
    {
      id: DEV_TOOLS_SECTION_ID.TRANSLATIONS,
      title: 'Translations 🌎',
      component: (
        <>
          <TranslationsForm
            onSubmit={({ debugLang }: { debugLang: string }) => setDebugLang(debugLang)}
            resetDebugLang={() => setDebugLang('')}
            debugLang={debugLang}
          />
          <ButtonsContainer>
            <SecondaryButton
              isSmall
              text='Go to Intl Debug Screen'
              onPress={() => handleNavigateAndClose(onNavigateToIntlDebugScreen)}
            />
            <SecondaryButton isSmall text='Clear Language Cache 🗑️' onPress={onClearLanguageCache} />
            <SecondaryButton isSmall text='Refresh Translations 🔄' onPress={onRefreshTranslations} />
          </ButtonsContainer>
        </>
      ),
      shouldShow: isMobile,
    },
    {
      id: DEV_TOOLS_SECTION_ID.AUTHENTICATION,
      title: 'Authentication 🛡️',
      component: (
        <AuthForm
          onSubmit={onAuthSubmit}
          idTokenExpiration={idTokenExpiration.toString()}
          inactivityExpiration={inactivityExpiration.toString()}
        />
      ),
      shouldShow: isMobile && isLoggedIn,
    },
    {
      id: DEV_TOOLS_SECTION_ID.PERSIST_STORE,
      title: 'Persist Store 🔒',
      component: persistItems && persistStoreKeys && (
        <PersistStoreForm
          persistItems={persistItems}
          onPersistStoreClearKeys={onPersistStoreClearKeys}
          onPersistStoreClearAll={onPersistStoreClearAll}
          persistStoreKeys={persistStoreKeys}
        />
      ),
      shouldShow: isMobile && persistItems,
    },
  ]

  const getDisplaySections = () => {
    const displaySections = sections.filter((section) => section.shouldShow)
    return displaySections.map((section, index) => {
      const { id, title, component } = section
      const subsections = 'subsections' in section ? section.subsections : undefined
      const getSectionContent = () => {
        return (
          <SectionContentContainer>
            {component}
            {subsections &&
              subsections.map((subsection: { id: string; title: string; component: React.JSX.Element }) => {
                return (
                  <SectionContainer key={subsection.title} testID={tID(`DevTools-subsection-${subsection.id}`)}>
                    <SectionTitle text={subsection.title} size={TitleSize.SMALL} color={colors.textPrimary} />
                    {subsection.component}
                  </SectionContainer>
                )
              })}
          </SectionContentContainer>
        )
      }
      return (
        <Accordion
          key={title}
          title={title}
          testIDPrefix={`DevTools-${id}`}
          content={getSectionContent()}
          initiallyOpen={index === 0 || title.includes('Search for Care')}
          showGradient={false}
        />
      )
    })
  }

  return (
    <Flex1SafeAreaView testID={tID('DevTools')} defaultFlex={!breakpoints.isMobileSized}>
      <MainHeader>
        <IconContainer testID={tID('DevTools-closeButton')} onPress={onClose}>
          <CloseIcon size={16} fillColor={colors.iconActive} />
        </IconContainer>
        <IconContainer testID={tID('DevTools-downloadButton')} onPress={onDownloadDevToolsData}>
          <DownloadFileIcon fill={colors.iconActive} />
        </IconContainer>
      </MainHeader>

      <ContentAreaView>
        <DevTitleContainer>
          <Title text='Developer Tools 👷🏻‍♂️ 🏗 🛠️' size={TitleSize.LARGE} />
        </DevTitleContainer>
        {getDisplaySections()}
      </ContentAreaView>
    </Flex1SafeAreaView>
  )
}
